import {
  Assay, IBiomaterial, IBiomaterialFilters, IPatient, ISubmitAssayData,
} from '@zero-dash/types';
import dayjs from 'dayjs';
import { useSnackbar } from 'notistack';
import {
  JSX, SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { Box, styled, type TabProps } from '@mui/material';
import { useOutletContext, useParams } from 'react-router-dom';
import { CustomTabs } from '@/components/Common/Tabs';
import { ScrollableSection } from '@/components/ScrollableSection/ScrollableSection';
import { corePalette } from '@/themes/colours';
import { useZeroDashSdk } from '@/contexts/ZeroDashSdkContext';
import { IDataFile } from '@/types/FileTracker/FileTracker.types';
import BiomaterialsTable from './BiomaterialsTable/BiomaterialsTable';
import { useIsUserAuthorised } from '@/hooks/useIsUserAuthorised';
import { usePatientTimelineData } from '@/hooks/PatientDashboard/usePatientTimelineData';
import LoadingAnimation from '@/components/Animations/LoadingAnimation';
import PatientTimeline from './Timeline/PatientTimeline';
import PatientSearchFilterBar from './SearchFilterSort/PatientSearchFilterBar';
import ClaritySubmissionsPanel from './ClaritySubmissions/ClaritySubmissionsPanel';
import ActionBar from './ActionBar/ActionBar';
import { IBiomaterialRow } from '@/types/PatientDashboard/TimelineView.types';
import WispSection from './Wisp/WispSection';
import TimelineNavBar from '@/components/PatientDashboard/TimelineTab/TimelineNavBar';
import updateTabTitle from '@/utils/functions/updateTabTitle';

const emptyOptions: IBiomaterialFilters = {
  search: [],
  specimenPurpose: [],
  subtype: [],
  inventoryStatus: [],
};

const CONTENT_GAP = '16px';
const STICKY_HEADER_Z_INDEX = 4;

const TIMELINE_SECTIONS = {
  WISP: 'wisp',
  CLARITY_MANAGER: 'clarity-manager',
} as const;

type TimelineSection = typeof TIMELINE_SECTIONS[keyof typeof TIMELINE_SECTIONS];

// The Clarity manager is the biomaterials table with its submissions view, moved under
// the timeline beside the WISP results rather than onto a page of its own.
const SECTION_TABS: TabProps[] = [
  { label: 'WISP', value: TIMELINE_SECTIONS.WISP },
  { label: 'Clarity Manager', value: TIMELINE_SECTIONS.CLARITY_MANAGER },
];

// SimpleBar nests a scroller of its own, and every bar and panel inside the tab nests
// one too, so this addresses the tab's scroller by its exact position rather than with a
// descendant selector that would restyle theirs as well.
const SIMPLEBAR_SCROLLER = '& > .simplebar-wrapper > .simplebar-mask > .simplebar-offset > .simplebar-content-wrapper';

const ScrollableContent = styled(ScrollableSection)({
  flex: 1,
  minHeight: 0,
  [SIMPLEBAR_SCROLLER]: {
    overflowX: 'hidden',
  },
});

// The column the tab's sections stack in. It lives inside the scroller rather than on
// SimpleBar's own content element, which the nested bars and panels share.
const ContentColumn = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: CONTENT_GAP,
  '& > *': {
    flexShrink: 0,
    minWidth: 0,
  },
});

// The section tabs and the filters pin as one opaque block above the timeline card, so
// switching section and filtering both stay reachable however far the page has scrolled.
const StickySectionHeader = styled(Box)({
  position: 'sticky',
  top: 0,
  zIndex: STICKY_HEADER_Z_INDEX,
  backgroundColor: corePalette.white,
  display: 'flex',
  flexDirection: 'column',
  gap: CONTENT_GAP,
});

export default function TimelineTabContent(): JSX.Element {
  const zeroDashSdk = useZeroDashSdk();
  const { enqueueSnackbar } = useSnackbar();
  const { publicSubjectId, patientId } = useParams();
  const { patientRecords, arePatientRecordsSettled } = useOutletContext<{
    patientRecords: IPatient[];
    arePatientRecordsSettled: boolean;
  }>();

  const [biomaterialsFailed, setBiomaterialsFailed] = useState(false);
  const [panelBuilderFiles, setPanelBuilderFiles] = useState<Record<string, IDataFile[]>>({});
  const [biomaterials, setBiomaterials] = useState<IBiomaterial[]>([]);
  const [hasFetchedBiomaterials, setHasFetchedBiomaterials] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [biomaterialFilters, setBiomaterialFilters] = useState<IBiomaterialFilters>(
    emptyOptions,
  );
  const biomaterialsRequestId = useRef(0);

  const [renderedSubjectId, setRenderedSubjectId] = useState(publicSubjectId);
  if (renderedSubjectId !== publicSubjectId) {
    setRenderedSubjectId(publicSubjectId);
    setBiomaterials([]);
    setHasFetchedBiomaterials(false);
    setBiomaterialsFailed(false);
  }

  const firstCollection = useMemo(() => (
    Array.from(new Set(biomaterials.map((b) => b.collectionDate)))
      .sort()
      .shift()
  ), [biomaterials]);

  const registrationDate = useMemo(() => (
    patientRecords
      .toSorted((a, b) => dayjs(a.registrationDate).diff(b.registrationDate))[0]
      ?.registrationDate
  ), [patientRecords]);

  const startingDate = registrationDate || firstCollection;

  const [rows, setRows] = useState<IBiomaterialRow[]>([]);
  const [selectedBiomaterialIds, setSelectedBiomaterialIds] = useState<number[]>([]);
  const [selectedAssays, setSelectedAssays] = useState<Assay[]>([]);
  const [dataToSubmit, setDataToSubmit] = useState<ISubmitAssayData[]>([]);
  const [showSubmissions, setShowSubmissions] = useState<boolean>(false);
  const [section, setSection] = useState<TimelineSection>(TIMELINE_SECTIONS.WISP);

  const userCanWrite = useIsUserAuthorised('biomaterial.write');

  const filterRow = useCallback((row: IBiomaterialRow): boolean => {
    const specimenPurposeMatches = !biomaterialFilters.specimenPurpose?.length
      || biomaterialFilters.specimenPurpose.includes(row.specimenPurpose || 'Not set');

    const subtypeMatches = !biomaterialFilters.subtype?.length
      || biomaterialFilters.subtype.includes(row.subtype || 'Not set');

    const normalize = (v: string): string => v.trim().toLowerCase();
    const searchMatches = !biomaterialFilters.search?.length
      || biomaterialFilters.search.some((term) => {
        const q = normalize(term);
        return normalize(row.name).includes(q)
          || normalize(row.id.toString()).includes(q);
      });

    const inventoryStatusMatches = !biomaterialFilters.inventoryStatus?.length
      || biomaterialFilters.inventoryStatus.includes(row.inventoryStatus || 'Not set');

    const containerTypeMatches = !biomaterialFilters.containerType?.length
      || biomaterialFilters.containerType.includes(row.containerType || 'Not set');

    const hasFiles = !biomaterialFilters.hasFiles
      || (row.panelBuilderFiles.length > 0);

    return specimenPurposeMatches
      && subtypeMatches
      && searchMatches
      && inventoryStatusMatches
      && containerTypeMatches
      && hasFiles;
  }, [biomaterialFilters]);

  const fetchBiomaterials = useCallback(async (): Promise<void> => {
    if (!patientRecords.length) {
      return;
    }

    biomaterialsRequestId.current += 1;
    const requestId = biomaterialsRequestId.current;
    const isCurrentRequest = (): boolean => biomaterialsRequestId.current === requestId;

    setLoading(true);
    try {
      const dedupPatientIds: [string, string][] = [...new Map(
        patientRecords.map((p): [string, [string, string]] => [
          `${p.patientId}::${p.lmSubjectId}`,
          [p.patientId, p.lmSubjectId.toString()],
        ]),
      ).values()];

      const resp = (await Promise.all(
        dedupPatientIds.map(([recordPatientId, lmSubjectId]) => zeroDashSdk
          .patient
          .getBiomaterialsByLmSubjId(recordPatientId, lmSubjectId)),
      )).flat();
      const formattedLmBiomaterials = [...new Map(
        resp.map((biomaterial) => [biomaterial.id, biomaterial]),
      )
        .values()]
        .toSorted((a, b) => a.name.localeCompare(
          b.name,
          undefined,
          { sensitivity: 'base' },
        ));
      if (isCurrentRequest()) {
        setBiomaterials(formattedLmBiomaterials);
        setBiomaterialsFailed(false);
      }
    } catch {
      if (isCurrentRequest()) {
        setBiomaterials([]);
        setBiomaterialsFailed(true);
      }
    } finally {
      if (isCurrentRequest()) {
        setLoading(false);
        setHasFetchedBiomaterials(true);
      }
    }
  }, [patientRecords, zeroDashSdk.patient]);

  const handleRetrySamples = useCallback((): void => {
    setBiomaterialsFailed(false);
    setHasFetchedBiomaterials(false);
    fetchBiomaterials();
  }, [fetchBiomaterials]);

  const {
    events,
    biosamples,
    reports,
    analysisSets,
    biomarker,
    diseaseBurden,
    isReportsLoading,
    retryBiomarkers,
    retryDiseaseBurden,
  } = usePatientTimelineData({
    publicSubjectId,
    patientRecords,
    arePatientRecordsSettled,
    biomaterials,
    hasFetchedBiomaterials,
    biomaterialsFailed,
    onRetrySamples: handleRetrySamples,
  });

  useEffect(() => {
    if (patientRecords.length) {
      return;
    }
    setHasFetchedBiomaterials(arePatientRecordsSettled);
  }, [arePatientRecordsSettled, patientRecords]);

  useEffect(() => {
    fetchBiomaterials();
  }, [fetchBiomaterials]);

  useEffect(() => {
    updateTabTitle({ patientId }, 'Timeline');
  }, [patientId]);

  useEffect(() => {
    if (biosamples.length) {
      zeroDashSdk.filetracker.getFilesByMapping({
        sampleIds: biosamples.map((b) => b.biosampleId),
        category: 'liquid_biopsy',
        mapping: {
          type: 'panel_builder',
        },
      })
        .then((resp) => {
          const map = resp.reduce(
            (acc, current) => {
              const biosample = biosamples.find((b) => b.biosampleId === current.sampleId);
              if (!biosample) return acc;
              return {
                ...acc,
                [biosample.biomaterialId]: [
                  ...(acc[biosample.biomaterialId] ?? []),
                  current,
                ],
              };
            },
            {} as Record<string, IDataFile[]>,
          );
          setPanelBuilderFiles(map);
        });
    } else {
      setPanelBuilderFiles({});
    }
  }, [biosamples, zeroDashSdk.filetracker]);

  useEffect(() => {
    const mapLmSubjIdToCCIAPatientId = (id: string): string => {
      const matchingPatientIds = patientRecords
        .filter((record) => record.lmSubjectId.toString() === id.toString())
        // Remove possible patientId duplicates
        .filter(
          (record, index, all) => all.findIndex((r) => r.patientId === record.patientId) === index,
        )
        .map((record) => record.patientId);

      if (!matchingPatientIds.length) return 'Unknown'; // Should never happen
      if (matchingPatientIds.length > 1) return matchingPatientIds.join(', ');

      return matchingPatientIds[0];
    };

    const dataGridRows: IBiomaterialRow[] = biomaterials
      .map((b) => ({
        name: b.name,
        specimenPurpose: b.specimenPurpose,
        id: b.id,
        patientId: mapLmSubjIdToCCIAPatientId(b.lmSubjectId),
        subtype: b.subtype,
        volume: b.volume,
        containerType: b.containerType,
        storageLocation: b.storageLocation,
        collectionDate: {
          date: b.collectionDate,
          daysFrom: dayjs(b.collectionDate)
            .startOf('day')
            .diff(dayjs(startingDate).startOf('day'), 'days'),
        },
        inventoryStatus: b.inventoryStatus,
        dateSentForSequencing: b.dateSentForSequencing,
        assaysOrdered: b.assaysOrdered,
        downstreamProcess: b.downstreamProcess,
        qlimsId: b.qlimsId,
        panelBuilderFiles: panelBuilderFiles[b.id] ?? [],
      }))
      .filter(filterRow);

    setRows(dataGridRows);
  }, [
    biomaterials,
    filterRow,
    panelBuilderFiles,
    patientRecords,
    startingDate,
  ]);

  const biomaterialSearchMethod = (query: string): void => {
    const delim = /\s*;\s*/;
    const terms = [...new Set(query.replace(/\n/g, ';').split(delim))]
      .map((v) => v.trim())
      .filter(Boolean);

    setBiomaterialFilters((prev) => ({
      ...prev,
      search: terms,
    }));
  };

  const handleRowSelect = (
    biomaterialId: number,
    isSelected: boolean,
  ): void => {
    setSelectedBiomaterialIds((prev) => (
      isSelected
        ? [...prev, biomaterialId]
        : prev.filter((id) => id !== biomaterialId)
    ));
    if (!isSelected) {
      setDataToSubmit((prev) => prev.filter((b) => b.biomaterialId !== biomaterialId));
    }
  };

  const handleClearSelection = (): void => {
    setSelectedBiomaterialIds([]);
    setSelectedAssays([]);
  };

  const handleSectionChange = (event: SyntheticEvent, value: TimelineSection): void => {
    setSection(value);
  };

  const handleToggleSubmissions = (): void => setShowSubmissions((previous) => !previous);

  const handleOnResetChanges = (): void => {
    setDataToSubmit([]);
  };

  const handleApplyAssays = (): void => {
    const data: ISubmitAssayData[] = biomaterials
      .filter((b) => selectedBiomaterialIds.includes(b.id))
      .map((b) => ({
        biomaterialId: b.id,
        assays: selectedAssays,
      }));

    const updatedIds = new Set(data.map((d) => d.biomaterialId));
    setDataToSubmit((prev) => [
      ...prev.filter((d) => !updatedIds.has(d.biomaterialId)),
      ...data,
    ]);
    handleClearSelection();
  };

  const handleSubmitAssays = async (name?: string): Promise<void> => {
    setLoading(true);
    try {
      const uniquePatientIds: [string, string][] = [];
      patientRecords.forEach((p) => {
        const stringifiedLmSubjId = p.lmSubjectId.toString();
        if (!uniquePatientIds.some((tuple) => tuple[1] === stringifiedLmSubjId)) {
          uniquePatientIds.push([
            p.patientId,
            stringifiedLmSubjId,
          ]);
        }
      });
      // Make one request for each different lmSubjId
      const resp = await Promise.all(
        uniquePatientIds.map(([recordPatientId, lmSubjId]) => {
          const assayDataForSubject = dataToSubmit.filter((data) => {
            const biomaterial = biomaterials.find((b) => b.id === data.biomaterialId);
            return biomaterial?.lmSubjectId?.toString() === lmSubjId;
          });

          // Case handling: for this lmSubjId there is nothing to submit
          if (!assayDataForSubject.length) return Promise.resolve([]);

          return zeroDashSdk.patient.submitBiomaterialsAssays(
            recordPatientId,
            lmSubjId,
            assayDataForSubject,
            name,
          );
        }),
      );

      const failures = resp.flat().filter((r) => r.status === 'error');
      if (failures.length) {
        enqueueSnackbar(
          `Could not submit assays for the following biomaterial ids: ${failures.map((f) => f.biomaterialId).join(', ')}`,
          { variant: 'error' },
        );
      } else {
        enqueueSnackbar('Assays successfully submitted', { variant: 'success' });
      }
      await fetchBiomaterials();
    } catch {
      enqueueSnackbar('Cannot submit assays, please try again', { variant: 'error' });
    } finally {
      setDataToSubmit([]);
      setLoading(false);
    }
  };

  const showSubmitActionBar = Boolean(dataToSubmit.length);
  const showAssaySelectionBar = Boolean(selectedBiomaterialIds.length);
  const isClarityManager = section === TIMELINE_SECTIONS.CLARITY_MANAGER;

  const biomaterialTable = loading ? (
    <LoadingAnimation />
  ) : (
    <Box width="100%">
      <BiomaterialsTable
        rows={rows}
        selectedBiomaterialIds={selectedBiomaterialIds}
        dataToSubmit={dataToSubmit}
        onToggle={handleRowSelect}
        canSelectBiomaterial={userCanWrite}
        search={biomaterialFilters.search}
      />
    </Box>
  );
  return (
    <>
      <TimelineNavBar patientRecords={patientRecords} />
      <ScrollableContent>
        <ContentColumn>
          <PatientTimeline
            key={publicSubjectId}
            patientId={patientId}
            registrationDate={registrationDate}
            firstCollection={firstCollection}
            biomaterials={biomaterials}
            biosamples={biosamples}
            events={events}
            reports={reports}
            isEventsLoading={events === undefined}
            isSamplesLoading={!hasFetchedBiomaterials}
            hasSamplesFailure={biomaterialsFailed}
            isReportsLoading={isReportsLoading}
            biomarker={biomarker}
            diseaseBurden={diseaseBurden}
            analysisSets={analysisSets}
            onRetrySamples={handleRetrySamples}
            onRetryBiomarkers={retryBiomarkers}
            onRetryDiseaseBurden={retryDiseaseBurden}
          />
          <StickySectionHeader>
            <CustomTabs
              variant="navigation"
              indicatorLocation="bottom"
              fullWidth
              tabs={SECTION_TABS}
              value={section}
              onChange={handleSectionChange}
              scrollButtons
              allowScrollButtonsMobile
            />
            {isClarityManager && (
              <PatientSearchFilterBar
                biomaterials={biomaterials}
                rows={rows}
                toggled={biomaterialFilters}
                setToggled={setBiomaterialFilters}
                emptyOptions={emptyOptions}
                loading={loading}
                setLoading={setLoading}
                counts={{ current: rows.length, total: biomaterials.length }}
                searchMethod={biomaterialSearchMethod}
                showSubmissions={showSubmissions}
                onToggleSubmissions={handleToggleSubmissions}
              />
            )}
          </StickySectionHeader>
          {isClarityManager ? (
            <>
              {showSubmissions
                ? (
                  <ClaritySubmissionsPanel
                    patientRecords={patientRecords}
                    biomaterials={biomaterials}
                  />
                ) : biomaterialTable}
              {(showAssaySelectionBar || showSubmitActionBar) && (
                <ActionBar
                  selectedBiomaterialIds={selectedBiomaterialIds}
                  onClose={handleClearSelection}
                  handleOnResetChanges={handleOnResetChanges}
                  canSelectAssays={userCanWrite}
                  selectedAssays={selectedAssays}
                  setSelectedAssays={setSelectedAssays}
                  showAssaySelectionBar={showAssaySelectionBar}
                  showSubmitActionBar={showSubmitActionBar}
                  onSubmit={handleSubmitAssays}
                  onApplyAssays={handleApplyAssays}
                  dataToSubmit={dataToSubmit}
                  biomaterials={biomaterials}
                  patientRecords={patientRecords}
                  canSubmitAssays={loading || showAssaySelectionBar || !userCanWrite}
                  resetDisabled={loading}
                />
              )}
            </>
          ) : (
            <WispSection
              analysisSets={analysisSets}
              diseaseBurden={diseaseBurden}
              biomaterials={biomaterials}
              onRetry={retryDiseaseBurden}
            />
          )}
        </ContentColumn>
      </ScrollableContent>
    </>
  );
}
